import { useState, useCallback, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import api from '@/lib/axios'

interface PostsIndexFilters {
  status: string | null
  search: string | null
  category_id: string | null
}

export function usePostsIndexVM(filters: PostsIndexFilters) {
  const [search, setSearchState] = useState(filters.search ?? '')
  const [statusFilter, setStatusFilter] = useState(filters.status ?? '')
  const [categoryFilter, setCategoryFilter] = useState(filters.category_id ?? '')
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const applyFilters = useCallback((newSearch: string, newStatus: string, newCategory: string) => {
    router.get('/admin/posts', {
      search: newSearch || undefined,
      status: newStatus || undefined,
      category_id: newCategory || undefined,
    }, { preserveState: true, replace: true })
  }, [])

  const setSearch = useCallback((val: string) => {
    setSearchState(val)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      applyFilters(val, statusFilter, categoryFilter)
    }, 400)
  }, [applyFilters, statusFilter, categoryFilter])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleStatusChange = (val: string) => {
    setStatusFilter(val)
    applyFilters(search, val, categoryFilter)
  }

  const handleCategoryChange = (val: string) => {
    setCategoryFilter(val)
    applyFilters(search, statusFilter, val)
  }

  const handlePageChange = (page: number) => {
    router.get('/admin/posts', {
      page,
      search: search || undefined,
      status: statusFilter || undefined,
      category_id: categoryFilter || undefined,
    }, { preserveState: true })
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.delete(`/admin/posts/${deleteId}`)
      setDeleteId(null)
      router.reload({ only: ['posts', 'pagination', 'stats'] })
    } finally {
      setDeleting(false)
    }
  }

  const deleteDialogOpen = deleteId !== null

  return {
    search,
    setSearch,
    statusFilter,
    categoryFilter,
    deleting,
    deleteDialogOpen,
    handleStatusChange,
    handleCategoryChange,
    handlePageChange,
    handleDelete,
    setDeleteId,
  }
}
